import React, { useState } from 'react';
import { MapContainer, TileLayer, Polyline, Popup, Marker } from 'react-leaflet';
import { Eye, X, Ruler, FileText } from 'lucide-react';
import { useObras } from '../../context/ObrasContextSupabase';
import L from 'leaflet';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

const MapaObras = () => {
  const { obras } = useObras();
  const [obraSelecionada, setObraSelecionada] = useState(null);

  // Coordenadas de Conceição do Castelo - ES
  const centroConceicaoCastelo = [-20.359292, -41.247282];

  const getCorPorStatus = (status) => {
    switch (status) {
      case 'Planejada':
        return '#ffc107'; // Amarelo
      case 'Em execução':
        return '#28a745'; // Verde
      case 'Finalizada':
        return '#007bff'; // Azul
      default:
        return '#6c757d'; // Cinza
    }
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  // Formatar distância para exibição (km com 2 casas decimais)
  const formatarDistancia = (metros) => {
    if (!metros) return '0 km';
    return `${(metros / 1000).toFixed(2)} km`;
  };

  // Função para calcular a distância total em metros de todos os trechos
  const calcularDistanciaTotal = (trechos) => {
    if (!trechos) return 0;
    return trechos.reduce((total, trecho) => {
      let distancia = trecho.distanciaMetros || 0;
      if (trecho.derivacoes) {
        distancia += trecho.derivacoes.reduce((subTotal, derivacao) =>
          subTotal + (derivacao.distanciaMetros || 0), 0);
      }
      return total + distancia;
    }, 0);
  };

  // Formatar percentual
  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };

  // Calcular valor total dos itens
  const calcularValorTotalItens = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((total, item) => total + (item.total || 0), 0);
  };

  // Calcular progresso geral dos itens
  const calcularProgressoGeral = (itens) => {
    if (!itens || itens.length === 0) return 0;

    const totalValor = calcularValorTotalItens(itens);
    if (totalValor === 0) return 0;

    const totalMedido = itens.reduce((total, item) => total + (item.totalMedido || 0), 0);
    return (totalMedido / totalValor) * 100;
  };

  // Função para determinar a cor da barra de progresso
  const getProgressColor = (progresso) => {
    if (progresso < 30) return '#dc3545'; // Vermelho
    if (progresso < 70) return '#ffc107'; // Amarelo
    return '#28a745'; // Verde
  };

  const abrirDetalhesObra = (obra) => {
    setObraSelecionada(obra);
  };

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header compacto */}
      <div style={{
        padding: '8px 15px',
        backgroundColor: 'white',
        borderBottom: '1px solid #dee2e6',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flexShrink: 0,
        zIndex: 1000
      }}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>Mapa das Obras</h2>
        <p style={{ color: '#666', margin: 0, fontSize: '12px' }}>
          Clique nos trajetos para informações.
          <span style={{ marginLeft: '8px' }}>
            <span style={{ color: '#ffc107', fontWeight: 'bold' }}>■ Planejada</span>
            <span style={{ color: '#28a745', fontWeight: 'bold', marginLeft: '6px' }}>■ Em execução</span>
            <span style={{ color: '#007bff', fontWeight: 'bold', marginLeft: '6px' }}>■ Finalizada</span>
          </span>
        </p>
      </div>

      {/* Mapa ocupando todo o espaço restante */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <MapContainer
          center={centroConceicaoCastelo}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {obras.map(obra => (
            <React.Fragment key={obra.id}>
              {/* Trechos da obra */}
              {obra.trechos && obra.trechos.map((trecho) => (
                <React.Fragment key={`${obra.id}-${trecho.id}`}>
                  {/* Trecho principal */}
                  <Polyline
                    positions={trecho.pontos}
                    color={getCorPorStatus(obra.status)}
                    weight={4}
                    opacity={0.8}
                  >
                    <Popup>
                      <div style={{ minWidth: '200px' }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{obra.nome}</h4>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Trecho:</strong> {trecho.nome}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Empreiteiro:</strong> {obra.empreiteiro}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Status:</strong> <span style={{ color: getCorPorStatus(obra.status) }}>{obra.status}</span>
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Valor:</strong> {formatarMoeda(obra.valor)}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Pontos:</strong> {trecho.pontos.length}
                        </p>
                        {trecho.distanciaMetros && (
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Extensão:</strong> <Ruler size={12} style={{ verticalAlign: 'middle', marginRight: '2px' }} />
                            {formatarDistancia(trecho.distanciaMetros)}
                          </p>
                        )}
                        {trecho.derivacoes && trecho.derivacoes.length > 0 && (
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Derivações:</strong> {trecho.derivacoes.length}
                          </p>
                        )}
                        <button
                          className="button"
                          style={{ marginTop: '10px', fontSize: '12px', padding: '6px 12px' }}
                          onClick={() => abrirDetalhesObra(obra)}
                        >
                          <Eye size={14} />
                          Ver Detalhes
                        </button>
                      </div>
                    </Popup>
                  </Polyline>

                  {/* Derivações do trecho */}
                  {trecho.derivacoes && trecho.derivacoes.map(derivacao => (
                    <Polyline
                      key={`${trecho.id}-${derivacao.id}`}
                      positions={derivacao.pontos}
                      color={getCorPorStatus(obra.status)}
                      weight={3}
                      opacity={0.7}
                      dashArray="5, 5"
                    >
                      <Popup>
                        <div style={{ minWidth: '200px' }}>
                          <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{obra.nome}</h4>
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Derivação:</strong> {derivacao.nome}
                          </p>
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Do trecho:</strong> {trecho.nome}
                          </p>
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Pontos:</strong> {derivacao.pontos.length}
                          </p>
                          {derivacao.distanciaMetros && (
                            <p style={{ margin: '5px 0', fontSize: '14px' }}>
                              <strong>Extensão:</strong> <Ruler size={12} style={{ verticalAlign: 'middle', marginRight: '2px' }} />
                              {formatarDistancia(derivacao.distanciaMetros)}
                            </p>
                          )}
                          <button
                            className="button"
                            style={{ marginTop: '10px', fontSize: '12px', padding: '6px 12px' }}
                            onClick={() => abrirDetalhesObra(obra)}
                          >
                            <Eye size={14} />
                            Ver Detalhes
                          </button>
                        </div>
                      </Popup>
                    </Polyline>
                  ))}

                  {/* Marcadores para início e fim de cada trecho */}
                  <Marker position={trecho.pontos[0]}>
                    <Popup>
                      <div>
                        <strong>Início:</strong> {trecho.nome}<br />
                        <strong>Obra:</strong> {obra.nome}
                      </div>
                    </Popup>
                  </Marker>

                  <Marker position={trecho.pontos[trecho.pontos.length - 1]}>
                    <Popup>
                      <div>
                        <strong>Fim:</strong> {trecho.nome}<br />
                        <strong>Obra:</strong> {obra.nome}
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </MapContainer>
      </div>

      {/* Modal de detalhes da obra */}
      {obraSelecionada && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Detalhes da Obra</h3>
              <button className="close-button" onClick={() => setObraSelecionada(null)}>
                <X size={20} />
              </button>
            </div>

            <div>
              <h4 style={{ color: '#333', marginBottom: '15px' }}>{obraSelecionada.nome}</h4>

              <div style={{ display: 'grid', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <strong>Empreiteiro:</strong> {obraSelecionada.empreiteiro}
                </div>
                <div>
                  <strong>Valor:</strong> {formatarMoeda(obraSelecionada.valor)}
                </div>
                <div>
                  <strong>Status:</strong>
                  <span
                    style={{
                      color: getCorPorStatus(obraSelecionada.status),
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}
                  >
                    {obraSelecionada.status}
                  </span>
                </div>
                <div>
                  <strong>Data de Início:</strong> {formatarData(obraSelecionada.dataInicio)}
                </div>
                <div>
                  <strong>Previsão de Término:</strong> {formatarData(obraSelecionada.dataPrevisaoTermino)}
                </div>
                {obraSelecionada.trechos && obraSelecionada.trechos.length > 0 && (
                  <div style={{
                    marginTop: '5px',
                    padding: '8px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '6px',
                    color: '#2e7d32',
                    fontWeight: 'bold'
                  }}>
                    <Ruler size={16} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
                    Extensão Total: {formatarDistancia(calcularDistanciaTotal(obraSelecionada.trechos))}
                  </div>
                )}
              </div>

              {/* Trechos da obra */}
              {obraSelecionada.trechos && obraSelecionada.trechos.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Trechos ({obraSelecionada.trechos.length}):</strong>
                  <div style={{ marginTop: '8px' }}>
                    {obraSelecionada.trechos.map((trecho) => (
                      <div key={trecho.id} style={{
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        marginBottom: '6px',
                        fontSize: '14px'
                      }}>
                        <div>
                          <strong>{trecho.nome}</strong> - {trecho.pontos.length} pontos
                          {trecho.distanciaMetros && (
                            <span style={{ marginLeft: '8px', color: '#2e7d32' }}>
                              • <Ruler size={12} style={{ verticalAlign: 'middle', marginRight: '2px' }} />
                              {formatarDistancia(trecho.distanciaMetros)}
                            </span>
                          )}
                        </div>
                        {trecho.derivacoes && trecho.derivacoes.length > 0 && (
                          <div style={{ marginTop: '4px', paddingLeft: '12px', fontSize: '12px', color: '#28a745' }}>
                            <strong>Derivações ({trecho.derivacoes.length}):</strong>
                            {trecho.derivacoes.map(derivacao => (
                              <div key={derivacao.id} style={{ marginLeft: '8px' }}>
                                • {derivacao.nome} ({derivacao.pontos.length} pontos)
                                {derivacao.distanciaMetros && (
                                  <span style={{ marginLeft: '5px', color: '#2e7d32' }}>
                                    • <Ruler size={10} style={{ verticalAlign: 'middle', marginRight: '2px' }} />
                                    {formatarDistancia(derivacao.distanciaMetros)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Itens e Medições da Obra */}
              {obraSelecionada.itens && obraSelecionada.itens.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <FileText size={18} />
                    <strong>Itens e Medições ({obraSelecionada.itens.length}):</strong>
                  </div>

                  {/* Resumo dos Itens */}
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '6px',
                    marginBottom: '10px'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <strong>Valor Total dos Itens:</strong> {formatarMoeda(calcularValorTotalItens(obraSelecionada.itens))}
                      </div>
                      <div>
                        <strong>Valor Medido:</strong> {formatarMoeda(obraSelecionada.itens.reduce((total, item) => total + (item.totalMedido || 0), 0))}
                      </div>
                    </div>

                    {/* Barra de Progresso */}
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <strong>Progresso Geral:</strong>
                        <span>{formatarPercentual(calcularProgressoGeral(obraSelecionada.itens))}</span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '20px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${calcularProgressoGeral(obraSelecionada.itens)}%`,
                          height: '100%',
                          backgroundColor: getProgressColor(calcularProgressoGeral(obraSelecionada.itens)),
                          borderRadius: '10px'
                        }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Lista de Itens Resumida */}
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {obraSelecionada.itens.map(item => (
                      <div key={item.id} style={{
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        marginBottom: '6px',
                        fontSize: '14px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <strong>{item.item} - {item.descricao}</strong>
                          </div>
                          <div>
                            {formatarMoeda(item.total)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          <div>
                            {item.quantidade} {item.unidade} x {formatarMoeda(item.preco)}
                          </div>
                          <div>
                            Medido: {formatarPercentual(item.progresso || 0)}
                          </div>
                        </div>

                        {/* Mini Barra de Progresso */}
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '3px',
                          overflow: 'hidden',
                          marginTop: '5px'
                        }}>
                          <div style={{
                            width: `${item.progresso || 0}%`,
                            height: '100%',
                            backgroundColor: getProgressColor(item.progresso || 0),
                            borderRadius: '3px'
                          }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {obraSelecionada.observacoes && (
                <div>
                  <strong>Observações:</strong>
                  <p style={{
                    marginTop: '8px',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    {obraSelecionada.observacoes}
                  </p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="button" onClick={() => setObraSelecionada(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapaObras;